import React, { useMemo, useState } from "react";
import { Calendar, dayjsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import dayjs from "dayjs";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { TopicInfoTrigger } from "./TopicInfoTrigger";
import { LevelIcon } from "./LevelBadge";

// react-big-calendar usa el locale GLOBAL de dayjs (se fija en 'es' en App.js).
const localizer = dayjsLocalizer(dayjs);

// Versión con arrastrar/redimensionar habilitado (docente/propietario). Solo se
// usa cuando el consumidor pasa handlers de edición; si no, el calendario es de
// solo lectura (estudiante).
const DnDCalendar = withDragAndDrop(Calendar);

const MESSAGES = {
  date: "Fecha",
  time: "Hora",
  event: "Clase",
  allDay: "Todo el día",
  week: "Semana",
  work_week: "Semana laboral",
  day: "Día",
  month: "Mes",
  previous: "Anterior",
  next: "Siguiente",
  yesterday: "Ayer",
  tomorrow: "Mañana",
  today: "Hoy",
  agenda: "Agenda",
  noEventsInRange: "No hay clases en este rango.",
  showMore: (total) => `+${total} más`,
};

// Día completo en las vistas de día/semana (00:00–23:59): no recortamos horas,
// para no ocultar clases de madrugada o de noche. El scroll se posiciona en la
// primera clase del rango (ver scrollToTime).
const DEFAULT_MIN = new Date(1970, 0, 1, 0, 0, 0);
const DEFAULT_MAX = new Date(1970, 0, 1, 23, 59, 59);

// Contenido del evento: título + inscritos (docente, D6) + aviso de tema
// repetido (estudiante, E6). Lee del resource lo que exista.
function EventContent({ event }) {
  const r = event.resource || {};
  // Docente: r.topics (TeacherPlanningSerializer). Estudiante: r.topicsDetail
  // (mapeado desde topics_detail al construir los eventos — ver
  // MyClassesPage.js). Cualquiera de los dos, o ninguno.
  const topics = r.topics ?? r.topicsDetail ?? [];
  return (
    <span className="d-inline-flex align-items-center gap-1" style={{ minWidth: 0 }}>
      {r.hasRepeats && (
        <i
          className="mdi mdi-repeat"
          title="Repite un tema que ya viste"
          aria-label="Repite un tema que ya viste"
        />
      )}
      <span className="text-truncate">{event.title}</span>
      <TopicInfoTrigger topics={topics} />
      <LevelIcon level={r.level} comparison={r.levelComparison} />
      {r.enrolled_count != null && (
        <span className="ms-1" style={{ opacity: 0.9, whiteSpace: "nowrap" }}>
          <i className="mdi mdi-account-multiple" aria-hidden="true" /> {r.enrolled_count}
        </span>
      )}
    </span>
  );
}

/**
 * Envoltura presentacional de react-big-calendar para las clases.
 * Recibe `events` con { id, title, start: Date, end: Date, resource }.
 * Reutilizable por docente y estudiante.
 */
function ClassesCalendar({
  events = [],
  onSelectEvent,
  eventPropGetter,
  // Sombreado de slots (día/semana), p. ej. fuera de la disponibilidad del docente.
  slotPropGetter,
  // Estilo por columna-día (día/semana). Recibe (date, resource): útil para tintar
  // grupos de recurso alternos (docentes) en la vista global del propietario.
  dayPropGetter,
  defaultView = "week",
  views = ["month", "week", "day", "agenda"],
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  height = 640,
  // Edición (docente/propietario). Si se pasa onEventDrop u onEventResize, el
  // calendario habilita arrastrar/redimensionar; onSelectSlot habilita crear por
  // selección de hueco. draggableAccessor decide qué eventos son movibles.
  onEventDrop,
  onEventResize,
  onSelectSlot,
  selectable = false,
  draggableAccessor,
  resizableAccessor,
  // Vista de recursos (columnas por docente/aula en día/semana). Si se pasan
  // `resources`, cada evento se ubica en su columna según resourceIdAccessor;
  // arrastrar entre columnas cambia el recurso (p. ej. reasignar instructor).
  resources,
  resourceIdAccessor,
  resourceTitleAccessor,
}) {
  // En móvil la vista Semana/Día es inusable (7 columnas muy angostas): se arranca
  // en Mes, más legible en pantallas pequeñas (como el calendario de Kapella).
  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 768;
  const initialView = isMobile && defaultView !== "agenda" ? "month" : defaultView;

  // Vista controlada: necesitamos saber la vista actual para desactivar el resize
  // en Mes (allí redimensionar extendería la clase a varios días, sin sentido).
  const [view, setView] = useState(initialView);

  // Abre la vista día/semana desplazada a la primera clase del rango (tope 6:00),
  // para no perder clases tempranas ni abrir en 00:00 con la pantalla vacía.
  const scrollToTime = useMemo(() => {
    let hour = 6;
    for (const e of events) {
      const start = e.start instanceof Date ? e.start : new Date(e.start);
      const h = start.getHours();
      if (h < hour) hour = h;
    }
    return new Date(1970, 0, 1, hour, 0, 0);
  }, [events]);

  const editable = Boolean(onEventDrop || onEventResize);
  const CalendarComp = editable ? DnDCalendar : Calendar;
  const dndProps = editable
    ? {
        onEventDrop,
        onEventResize,
        // En Mes se desactiva el resize (extendería la clase al día siguiente);
        // el arrastre para mover sí sigue disponible en todas las vistas.
        resizable: Boolean(onEventResize) && view !== "month",
        draggableAccessor: draggableAccessor ?? (() => true),
        resizableAccessor: resizableAccessor ?? draggableAccessor ?? (() => true),
      }
    : {};

  return (
    <div style={{ height }}>
      <CalendarComp
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        views={views}
        view={view}
        onView={setView}
        scrollToTime={scrollToTime}
        onSelectEvent={onSelectEvent}
        onSelectSlot={onSelectSlot}
        selectable={selectable}
        eventPropGetter={eventPropGetter}
        slotPropGetter={slotPropGetter}
        dayPropGetter={dayPropGetter}
        components={{ event: EventContent }}
        messages={MESSAGES}
        min={min}
        max={max}
        resources={resources}
        resourceIdAccessor={resourceIdAccessor}
        resourceTitleAccessor={resourceTitleAccessor}
        popup
        style={{ height: "100%" }}
        {...dndProps}
      />
    </div>
  );
}

export { ClassesCalendar };
